import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyAppInstallationKey,
  ShopifyAppInstallationToAppKey,
  ShopifyAppKey,
  ShopifyAppToFulfillmentHoldKey,
  ShopifyAppToShopifyAppInstallationsKey,
  ShopifyFulfillmentHoldKey,
  ShopifyFulfillmentHoldToAppKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyAppKey,
  "2022-01": {
    name: "Shopify App",
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id" } },
      },
      {
        name: "API Key",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "apiKey" } },
      },
      {
        name: "App Store App URL",
        fieldType: FieldType.URL,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "appStoreAppUrl" } },
      },
      {
        name: "App Store Developer URL",
        fieldType: FieldType.URL,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "appStoreDeveloperUrl" } },
      },
      {
        name: "Available Access Scopes",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "availableAccessScopes" } },
      },
      // {
      //   name: "Banner",
      //   fieldType: FieldType.BelongsTo,
      //   key: ShopifyAppToBannerKey,
      //   contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "banner" } },
      // },
      {
        name: "Description",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "description" } },
      },
      {
        name: "Developer Name",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "developerName" } },
      },
      {
        name: "Developer Type",
        fieldType: FieldType.Enum,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "developerType" } },
        configuration: {
          allowMultiple: false,
          options: [{ name: "MERCHANT" }, { name: "PARTNER" }, { name: "SHOPIFY" }, { name: "UNKNOWN" }],
        },
      },
      {
        name: "Embedded",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "embedded" } },
      },
      {
        name: "Failed Requirements",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "failedRequirements" } },
      },
      {
        name: "Features",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "features" } },
      },
      {
        name: "Feedback",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "feedback" } },
      },
      {
        name: "Handle",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "handle" } },
      },
      // {
      //   name: "Icon",
      //   fieldType: FieldType.BelongsTo,
      //   key: ShopifyAppToIconKey,
      //   contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "icon" } },
      // },
      // {
      //   name: "Install URL",
      //   fieldType: FieldType.URL,
      //   contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "installUrl" } },
      // },
      // {
      //   name: "Is Post Purchase App In Use",
      //   fieldType: FieldType.Boolean,
      //   contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "isPostPurchaseAppInUse" } },
      // },
      {
        name: "Pricing Details",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "pricingDetails" } },
      },
      {
        name: "Pricing Details Summary",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "pricingDetailsSummary" } },
      },
      {
        name: "Privacy Policy URL",
        fieldType: FieldType.URL,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "privacyPolicyUrl" } },
      },
      {
        name: "Public Category",
        fieldType: FieldType.Enum,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "publicCategory" } },
        configuration: {
          allowMultiple: false,
          options: [{ name: "CUSTOM" }, { name: "OTHER" }, { name: "PRIVATE" }, { name: "PUBLIC" }],
        },
      },
      {
        name: "Published",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "published" } },
      },
      // {
      //   name: "Screenshots",
      //   fieldType: FieldType.HasMany,
      //   contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "screenshots" } },
      //   configuration: {
      //     relatedModelKey: ShopifyImageKey,
      //     inverseFieldKey: ShopifyImageToShopifyAppKey,
      //   },
      // },
      {
        name: "Shopify Developed",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "shopifyDeveloped" } },
      },
      {
        name: "Title",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "title" } },
      },
      {
        name: "Uninstall Message",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "uninstallMessage" } },
      },
      {
        name: "Webhook API Version",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "webhookApiVersion" } },
      },
      {
        name: "Installations",
        fieldType: FieldType.HasMany,
        key: ShopifyAppToShopifyAppInstallationsKey,
        configuration: {
          relatedModelKey: ShopifyAppInstallationKey,
          inverseFieldKey: ShopifyAppInstallationToAppKey,
        },
      },
    ],
    syncConfig: { type: "custom", resource: "app" },
  },
  "2022-04": {
    fields: [
      {
        name: "Previously Installed",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "previouslyInstalled" } },
      },
      {
        name: "Requested Access Scopes",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "requestedAccessScopes" } },
      },
    ],
  },
  "2025-04": {
    fields: [
      {
        name: "Fulfillment Holds",
        fieldType: FieldType.HasMany,
        key: ShopifyAppToFulfillmentHoldKey,
        configuration: {
          dependent: "delete",
          relatedModelKey: ShopifyFulfillmentHoldKey,
          inverseFieldKey: ShopifyFulfillmentHoldToAppKey,
        },
      },
    ],
  },
};

export const ShopifyApp = ({
  apiVersion,
  frameworkVersionConstraint,
  webhookTopicsForModel,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
  webhookTopicsForModel?: string[];
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint, webhookTopicsForModel);
